import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { useParams } from 'react-router';
import { getMasterAdminOrganizationDetailsPath } from '@/bundle/_MasterAdmin/path/path';
import LogoSmallIcon from '@/bundle/_Opco/_OpcoUsersPage/OpcoUserDetailsPage/images/LogoSmall.svg?react';
import { Box } from '@/components/Box/Box';
import { UserDetailsHeader } from '@/bundle/shared/components/UserDetails/UserDetailsHeader/UserDetailsHeader';
import { InviteNotification } from '@/bundle/_Opco/ui/OrganizationDetails/Details/ui/OrganizationUserAccountDetails/styles';
import { Notification } from '@/components/Notification/Notification';
import { NotificationMessage, NotificationTitle } from '@/components/Notification/styles';
import { RESEND_INVITATION_NOTIFICATION } from '@/const/shared';
import { AdminPersonalDetails } from '@/bundle/_MasterAdmin/_OrganizationUserDetailsPage/ui/AdminPersonalDetails';
import { AdminAccountDetails } from './ui/AdminAccountDetails';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@/context/userContext';
import { getInternalOrganizationUserDetails } from './api/api';
import { isInvitedOrganizationUser } from '@/helpers/organizationHelpers';
import { ProfileCookiesPreferences } from '@/bundle/shared/components/ProfileCookiesPreferences/ProfileCookiesPreferences';
import { useStatefulNavigation } from '@/hooks/useStatefulNavigation';

export const OrganizationUserDetailsPage = () => {
  const queryClient = useQueryClient();
  const { navigateBack } = useStatefulNavigation();
  const { id } = useParams<{ id: string }>();
  const { user } = useUser();

  const { data } = useQuery({
    queryKey: ['get_internal_organization_user_details', id],
    queryFn: () => getInternalOrganizationUserDetails(id),
  });

  const userDetails = data?.body;
  const isOwnAccount = user?.id === userDetails?.id;
  const isInvited = isInvitedOrganizationUser(userDetails?.status);

  const refetchUser = () => queryClient.invalidateQueries({ queryKey: ['get_internal_organization_user_details', id] });

  return (
    <ContentLayout
      title='Org User Details'
      backButton='Back to List'
      breadcrumbs='Organization Details > User Details'
      onBack={() => navigateBack(getMasterAdminOrganizationDetailsPath())}
    >
      <PageBody>
        <Box width='50%' mobile={{ width: '100%' }}>
          <UserDetailsHeader
            title='WireVault Account Information'
            icon={<LogoSmallIcon />}
            isOwnAccount={isOwnAccount}
          />
        </Box>
        <Box
          width={isInvited ? '100%' : '50%'}
          display='flex'
          flexDirection='row'
          columnGap='28px'
          mobile={{ width: '100%', flexDirection: 'column-reverse', rowGap: '42px' }}
        >
          {userDetails && (
            <Box width='100%' display='flex' flexDirection='column' rowGap='42px'>
              <Box width='100%' flexDirection='column' rowGap='58px' mobile={{ rowGap: '42px' }}>
                <AdminAccountDetails user={userDetails} onRefetchUser={refetchUser} isOwnAccount={isOwnAccount} />
                <AdminPersonalDetails user={userDetails} onRefetchUser={refetchUser} isOwnAccount={isOwnAccount} />
              </Box>
              {isOwnAccount && <ProfileCookiesPreferences />}
            </Box>
          )}
          {isInvited && (
            <Box width='100%'>
              <InviteNotification>
                <Notification>
                  <NotificationTitle>The invitation to WireVault has not been accepted.</NotificationTitle>
                  <NotificationMessage>
                    Please be sure the email address and mobile phone number are correct. You can change the information
                    for this account until the invitation is accepted.
                    <Box marginTop='12px'>{RESEND_INVITATION_NOTIFICATION}</Box>
                  </NotificationMessage>
                </Notification>
              </InviteNotification>
            </Box>
          )}
        </Box>
      </PageBody>
    </ContentLayout>
  );
};
